/**
 * Map a shop_profile.hours object to day-number-keyed open/close windows for the
 * appointment calendar.
 *
 * CASE-INSENSITIVE and numeric-key tolerant. The canonical hours shape written by
 * onboarding uses LOWERCASE day-name keys (see ./defaults.ts). A capitalized-only
 * lookup previously dropped every lowercase key, leaving the map empty and
 * rendering the whole calendar "Closed" for validly-configured shops — which
 * blocked appointment selection. This helper accepts any-case day names and
 * numeric-string keys ('0'..'6'), matching the case-tolerant reads already used
 * by shopHoursUtils.ts and ShopProfile.tsx.
 */
export type DayWindow = { open: string; close: string };

const DAY_NAME_TO_NUM: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
};

/** Resolve a shop_profile.hours key to a day number (0=Sun..6=Sat), or undefined. */
export function dayNumberForKey(key: string): number | undefined {
  const norm = String(key).trim().toLowerCase();
  if (/^[0-6]$/.test(norm)) return Number(norm);
  return DAY_NAME_TO_NUM[norm];
}

export function mapShopHoursToDayNumbers(hoursData: unknown): Record<number, DayWindow> {
  const mapped: Record<number, DayWindow> = {};
  if (!hoursData || typeof hoursData !== 'object') return mapped;
  for (const [key, value] of Object.entries(hoursData as Record<string, unknown>)) {
    const dayNum = dayNumberForKey(key);
    const win = value as { open?: string; close?: string } | null;
    if (dayNum !== undefined && win?.open && win?.close) {
      mapped[dayNum] = { open: win.open, close: win.close };
    }
  }
  return mapped;
}
